import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, MapPin, MessageCircle, Users, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function fmtQty(n, lang) {
  return new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(n || 0);
}

export function fmtDate(iso, lang) {
  if (!iso) return '';
  return new Date(iso.length === 10 ? `${iso}T00:00:00` : iso)
    .toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function unitLabel(t, unit) {
  return t(`produceUnits.${unit}`, unit);
}

/** The unit as in "per tonne". */
export function unitOne(t, unit) {
  return t(`produceUnitOne.${unit}`, unit);
}

/** Price line: one price, a range (clusters), or "ask the seller". */
export function priceText(t, lang, { price_min: min, price_max: max, unit }) {
  if (min == null) return t('produceMarket.market.priceAsk');
  const u = unitOne(t, unit);
  if (max != null && max !== min) {
    return t('produceMarket.market.priceRange', { min: fmtQty(min, lang), max: fmtQty(max, lang), unit: u });
  }
  return t('produceMarket.market.price', { price: fmtQty(min, lang), unit: u });
}

/**
 * One offer on the produce market: a farmer's own stock, or a cluster's
 * total. The stock level is the biggest line, as offtakers search by it.
 * "Chat with" names who answers: the farmer, or the cluster's manager.
 */
export default function OfferCard({ offer, onMessage }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isCluster = offer.kind === 'cluster';
  return (
    <div className="rounded-2xl border bg-white p-4" data-testid={`offer-${offer.kind}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-heading font-bold text-lg break-words">{offer.item}</p>
          <p className="text-sm text-gray-600 flex items-center gap-1 break-words">
            {isCluster ? <Users className="w-4 h-4 shrink-0" /> : <User className="w-4 h-4 shrink-0" />}
            {isCluster ? offer.cluster_name : offer.contact_name}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
          isCluster ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
          {isCluster ? t('produceMarket.market.cluster') : t('produceMarket.market.farmer')}
        </span>
      </div>
      <p className="mt-2 text-2xl font-extrabold text-farm-primary" data-testid="offer-stock">
        {t('produceMarket.market.available', { qty: fmtQty(offer.quantity, lang), unit: unitLabel(t, offer.unit) })}
      </p>
      {isCluster && (
        <p className="text-sm text-gray-600">{t('produceMarket.market.fromFarmers', { n: offer.farmers })}</p>
      )}
      <p className="text-sm font-semibold mt-1">{priceText(t, lang, offer)}</p>
      <div className="mt-2 space-y-0.5 text-sm text-gray-600">
        <p className="flex items-center gap-1"><CalendarClock className="w-4 h-4" />
          {t('produceMarket.market.bestBefore', { date: fmtDate(offer.best_before, lang) })}
        </p>
        <p className="text-xs text-gray-400 pl-5">{t('produceMarket.market.updated', { date: fmtDate(offer.updated_at, lang) })}</p>
        {offer.location && <p className="flex items-center gap-1"><MapPin className="w-4 h-4" />{offer.location}</p>}
      </div>
      {offer.can_chat && onMessage && (
        <Button className="w-full h-11 mt-3 bg-farm-primary hover:bg-farm-primary-dark" onClick={() => onMessage(offer)}
                data-testid="offer-message">
          <MessageCircle className="w-4 h-4 mr-2" /> {t('produceMarket.market.chatWith', { name: offer.contact_name })}
        </Button>
      )}
    </div>
  );
}
